import { fixedUi } from '@/i18n/fixedUi';
import { useCallback, useState } from 'react';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import {
  buildMaterialTemplateExportBundle,
  downloadCsvFromObjects,
  downloadJson,
  fetchTemplatesForExport,
  importMaterialTemplates,
  materialTemplateTypeLabel,
  parseMaterialTemplateImportBundle,
  pickJsonFile,
} from '@/temu-platform/utils/temuMaterialTemplatePortable';
import { portableItemsToCreateBodies } from '@/temu-platform/utils/temuOfficialTemplateBatchImport';
import { showToast } from '@/utils/toast';

/**
 * 素材模板列表页共用：CSV / JSON 导出 + JSON 导入。
 * CSV 支持筛选 / 本页 / 选中；JSON 仍按「有选中则选中，否则本页」。
 * @param {() => Promise<string|null>} [resolveShopId] 全部视图下导入前解析目标店
 * @param {(body: object, ctx: { shopId: string }) => Promise<any>} createOne
 * @param {(payload: object, ctx: { shopId: string }) => Promise<any>} [batchCreate]
 * @param {() => any[]} [resolvePageRows] 本页行
 * @param {() => any[]} [resolveSelectedRows] 选中行
 * @param {() => any[]|Promise<any[]>} [resolveFilteredRows] 当前筛选全量（可异步翻页）
 * @param {() => any[]} [resolveExportRows] 兼容旧用法：选中或本页（JSON 默认也用此逻辑）
 */
export default function useMaterialTemplatePortableActions({
  type,
  shopId,
  resolveShopId,
  filePrefix,
  resolveExportRows,
  resolvePageRows,
  resolveSelectedRows,
  resolveFilteredRows,
  buildCsvRows,
  getOne,
  listByProductType,
  createOne,
  batchCreate,
  importTargetLabel = '当前店铺',
  onImported,
}) {
  const [porting, setPorting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const typeLabel = materialTemplateTypeLabel(type);

  const resolveImportShopId = useCallback(async () => {
    if (typeof resolveShopId === 'function') {
      return resolveShopId();
    }
    return shopId || null;
  }, [resolveShopId, shopId]);

  const runCsvExport = useCallback((rows) => {
    const exportRows = typeof buildCsvRows === 'function' ? buildCsvRows(rows || []) : [];
    if (!exportRows.length) {
      showToast(fixedUi("暂无数据可导出"), 'warning');
      return;
    }
    downloadCsvFromObjects(exportRows, `${filePrefix || typeLabel}_${Date.now()}.csv`);
    showToast(`已导出 ${exportRows.length} 条`, 'success');
  }, [buildCsvRows, filePrefix, typeLabel]);

  const resolveJsonRows = useCallback(() => {
    if (typeof resolveExportRows === 'function') {
      return resolveExportRows() || [];
    }
    const selected = typeof resolveSelectedRows === 'function' ? resolveSelectedRows() : [];
    if (selected.length) return selected;
    return typeof resolvePageRows === 'function' ? (resolvePageRows() || []) : [];
  }, [resolveExportRows, resolveSelectedRows, resolvePageRows]);

  /** @deprecated 兼容 Admin 等旧页：选中或本页 */
  const onExportCsv = useCallback(() => {
    runCsvExport(resolveJsonRows());
  }, [runCsvExport, resolveJsonRows]);

  const onExportCsvPage = useCallback(() => {
    const rows = typeof resolvePageRows === 'function'
      ? resolvePageRows()
      : resolveJsonRows();
    runCsvExport(rows || []);
  }, [resolvePageRows, resolveJsonRows, runCsvExport]);

  const onExportCsvSelected = useCallback(() => {
    const rows = typeof resolveSelectedRows === 'function' ? resolveSelectedRows() : [];
    if (!rows?.length) {
      showToast(fixedUi("请先勾选要导出的数据"), 'warning');
      return;
    }
    runCsvExport(rows);
  }, [resolveSelectedRows, runCsvExport]);

  const onExportCsvFiltered = useCallback(async () => {
    if (exporting || porting) return;
    setExporting(true);
    try {
      let rows;
      if (typeof resolveFilteredRows === 'function') {
        rows = await Promise.resolve(resolveFilteredRows());
      } else if (typeof resolvePageRows === 'function') {
        rows = resolvePageRows();
      } else {
        rows = resolveJsonRows();
      }
      runCsvExport(Array.isArray(rows) ? rows : []);
    } catch (e) {
      showToast(e?.message || fixedUi("导出筛选失败"), 'error');
    } finally {
      setExporting(false);
    }
  }, [exporting, porting, resolveFilteredRows, resolvePageRows, resolveJsonRows, runCsvExport]);

  const onExportJson = useCallback(async () => {
    if (!shopId) {
      showToast(fixedUi("请先选择店铺"), 'warning');
      return;
    }
    const rows = resolveJsonRows();
    if (!rows.length) {
      showToast(fixedUi("暂无数据可导出"), 'warning');
      return;
    }
    setPorting(true);
    try {
      const details = await fetchTemplatesForExport(rows, getOne);
      const bundle = buildMaterialTemplateExportBundle({
        type,
        shopId,
        items: details,
      });
      downloadJson(bundle, `${filePrefix || typeLabel}_${Date.now()}.json`);
      showToast(`已导出完整 JSON ${details.length} 条`, 'success');
    } catch (e) {
      showToast(e?.message || '导出失败', 'error');
    } finally {
      setPorting(false);
    }
  }, [shopId, resolveJsonRows, getOne, type, filePrefix, typeLabel]);

  const onImportJson = useCallback(async () => {
    const sid = await resolveImportShopId();
    if (!sid) {
      showToast(fixedUi("请先选择店铺"), 'warning');
      return;
    }
    let picked;
    try {
      picked = await pickJsonFile();
    } catch (e) {
      // 用户取消选择时不提示
      if (e?.message && e.message !== '未选择文件') {
        showToast(e.message, 'error');
      }
      return;
    }
    let bundle;
    try {
      bundle = parseMaterialTemplateImportBundle(picked.data, type);
    } catch (e) {
      showToast(e?.message || '文件校验失败', 'error');
      return;
    }
    const confirmed = await showTemuConfirm({
      title: `导入${typeLabel}`,
      message: `将导入 ${bundle.items.length} 条到${importTargetLabel}。优先级会自动重算以避免冲突，模板名称保持文件中的原名。是否继续？`,
      confirmText: '开始导入',
      cancelText: '取消',
      tone: 'warning',
    });
    if (!confirmed) return;

    const ctx = { shopId: String(sid) };
    setPorting(true);
    try {
      let result;
      if (typeof batchCreate === 'function') {
        const res = await batchCreate({
          items: portableItemsToCreateBodies(type, bundle.items),
        }, ctx);
        const failed = Array.isArray(res?.failed) ? res.failed : [];
        result = {
          ok: Number(res?.created) || 0,
          failed: failed.length,
          errors: failed.map((row) => ({
            index: row.index,
            name: row.name,
            message: row.message,
          })),
        };
      } else {
        result = await importMaterialTemplates({
          type,
          items: bundle.items,
          listByProductType: listByProductType
            ? (productTypeId) => listByProductType(productTypeId, ctx)
            : undefined,
          createOne: (body) => createOne(body, ctx),
        });
      }
      if (result.ok > 0) {
        await onImported?.();
      }
      if (result.failed === 0) {
        showToast(`成功导入 ${result.ok} 条`, 'success');
      } else if (result.ok === 0) {
        showToast(result.errors[0]?.message || '导入失败', 'error');
      } else {
        showToast(`导入完成：成功 ${result.ok}，失败 ${result.failed}`, 'warning');
      }
    } catch (e) {
      showToast(e?.message || '导入失败', 'error');
    } finally {
      setPorting(false);
    }
  }, [
    resolveImportShopId,
    type,
    typeLabel,
    listByProductType,
    createOne,
    batchCreate,
    importTargetLabel,
    onImported,
  ]);

  return {
    porting,
    exporting,
    onExportCsv,
    onExportCsvFiltered,
    onExportCsvPage,
    onExportCsvSelected,
    onExportJson,
    onImportJson,
  };
}
